import { api } from '../lib/api-client';
import { readApiResult, type DataResult } from '../lib/data-result';
import { writeIssuePageDataCache } from '../lib/issue-page-data-cache';
import type { Notification, NotificationReadResult, NotificationsReadAllResult, PaginatedData } from '../types/api';

export type NotificationFilter = 'all' | 'unread';

export interface NotificationsViewData {
  readonly items: readonly Notification[];
  readonly total: number;
  readonly unreadCount: number;
  readonly filter: NotificationFilter;
}

export interface NotificationDestinationAction {
  readonly label: string;
  readonly destinationLabel: string;
  readonly iconClass: string;
  readonly statusLabel: string;
  readonly openingStatusLabel: string;
}

type NotificationsApiData = PaginatedData<Notification> & {
  readonly unread_count?: number;
};

const prefetchedIssueIds = new Set<number>();

function escapeNotificationAttr(value: string): string {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function notificationIcon(type: string): string {
  switch (type) {
    case 'issue_status': return 'fa-circle-dot text-brand-400';
    case 'comment': return 'fa-comment text-cyan-400';
    case 'skill_published': return 'fa-box-open text-emerald-400';
    case 'new_watcher': return 'fa-eye text-purple-400';
    case 'review_requested': return 'fa-clipboard-list text-amber-400';
    case 'pr_merged': return 'fa-check-double text-emerald-400';
    case 'changes_requested': return 'fa-rotate-left text-amber-400';
    case 'pr_submitted': return 'fa-code-branch text-blue-400';
    case 'account_update': return 'fa-user-gear text-blue-400';
    default: return 'fa-bell text-zinc-400';
  }
}

export function notificationFilterFromSearch(search: string): NotificationFilter {
  const params = new URLSearchParams(search);
  if (params.get('unread') === 'true' || params.get('filter') === 'unread') return 'unread';
  return 'all';
}

export function buildNotificationFilterSearch(filter: NotificationFilter): string {
  if (filter === 'unread') return '?unread=true';
  return '';
}

function issueIdFromPathname(pathname: string): number | null {
  const match = pathname.match(/^\/issues\/([1-9]\d*)\/?$/);
  if (!match) return null;
  const parsed = Number(match[1]);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function pathnameFromNotificationLink(link?: string | null): string | null {
  const rawLink = typeof link === 'string' ? link.trim() : '';
  if (!rawLink) return null;
  try {
    const baseUrl = typeof window === 'undefined' ? 'http://skillfab.local' : window.location.origin;
    return new URL(rawLink, baseUrl).pathname;
  } catch {
    return null;
  }
}

export function issueIdFromNotificationLink(link?: string | null): number | null {
  const pathname = pathnameFromNotificationLink(link);
  return pathname ? issueIdFromPathname(pathname) : null;
}

export function prefetchIssuePageDataFromNotificationLink(link?: string | null): boolean {
  const issueId = issueIdFromNotificationLink(link);
  if (issueId === null || prefetchedIssueIds.has(issueId)) return false;
  prefetchedIssueIds.add(issueId);
  void api.issues.getPageData(issueId).then((result) => {
    if (result.success) {
      writeIssuePageDataCache(issueId, result.data);
      return;
    }
    prefetchedIssueIds.delete(issueId);
  });
  return true;
}

export function renderNotificationSummary(input: Pick<NotificationsViewData, 'total' | 'unreadCount' | 'filter'>): string {
  if (input.filter === 'unread') {
    return input.total === 1 ? '1 unread notification' : `${input.total} unread notifications`;
  }
  if (input.unreadCount > 0) {
    return input.unreadCount === 1
      ? `1 unread · ${input.total} total`
      : `${input.unreadCount} unread · ${input.total} total`;
  }
  return input.total === 1 ? '1 notification' : `${input.total} notifications`;
}

export function notificationDestinationAction(link?: string | null): NotificationDestinationAction {
  const pathname = pathnameFromNotificationLink(link);
  if (!pathname) {
    return {
      label: 'No destination',
      destinationLabel: 'No linked page',
      iconClass: 'fa-circle-info',
      statusLabel: 'No action needed',
      openingStatusLabel: 'No destination',
    };
  }

  if (/^\/issues\/[1-9]\d*(?:\/)?$/.test(pathname)) {
    return {
      label: 'Open issue',
      destinationLabel: 'Issue detail',
      iconClass: 'fa-circle-dot',
      statusLabel: 'Issue page data warms in background',
      openingStatusLabel: 'Opening issue...',
    };
  }
  if (/^\/issues\/[1-9]\d*\/board(?:\/)?$/.test(pathname)) {
    return {
      label: 'Open board',
      destinationLabel: 'Issue board',
      iconClass: 'fa-table-columns',
      statusLabel: 'Opens immediately',
      openingStatusLabel: 'Opening board...',
    };
  }
  if (/^\/issues\/[1-9]\d*\/code(?:\/)?$/.test(pathname)) {
    return {
      label: 'Open code',
      destinationLabel: 'Issue code',
      iconClass: 'fa-code',
      statusLabel: 'Opens immediately',
      openingStatusLabel: 'Opening code view...',
    };
  }
  if (/^\/submissions\/[1-9]\d*(?:\/)?$/.test(pathname)) {
    return {
      label: 'Open submission',
      destinationLabel: 'Submission detail',
      iconClass: 'fa-clipboard-check',
      statusLabel: 'Review context opens immediately',
      openingStatusLabel: 'Opening submission...',
    };
  }
  if (/^\/skills\/[^/]+(?:\/)?$/.test(pathname)) {
    return {
      label: 'Open skill',
      destinationLabel: 'Published skill',
      iconClass: 'fa-box-open',
      statusLabel: 'Skill facts open immediately',
      openingStatusLabel: 'Opening skill...',
    };
  }
  if (/^\/settings(?:\/)?$/.test(pathname)) {
    return {
      label: 'Open settings',
      destinationLabel: 'Account settings',
      iconClass: 'fa-user-gear',
      statusLabel: 'Opens immediately',
      openingStatusLabel: 'Opening settings...',
    };
  }
  if (/^\/dashboard(?:\/)?$/.test(pathname)) {
    return {
      label: 'Open dashboard',
      destinationLabel: 'Dashboard',
      iconClass: 'fa-table-list',
      statusLabel: 'Work queue opens immediately',
      openingStatusLabel: 'Opening dashboard...',
    };
  }

  return {
    label: 'Open destination',
    destinationLabel: 'Linked page',
    iconClass: 'fa-arrow-up-right-from-square',
    statusLabel: 'Opens immediately',
    openingStatusLabel: 'Opening destination...',
  };
}

export function renderNotificationViewLink(input: { id: number; link?: string | null; isRead: boolean }): string {
  if (!input.link) return '';
  const href = escapeNotificationAttr(input.link);
  const action = notificationDestinationAction(input.link);
  const className = 'inline-flex items-center gap-1.5 text-xs font-medium text-brand-400 hover:text-brand-300';
  const content = '<i class="fas ' + escapeNotificationAttr(action.iconClass) + '" aria-hidden="true"></i>' + escapeNotificationAttr(action.label);
  const describedBy = ' aria-describedby="notif-' + escapeNotificationAttr(String(input.id)) + '-action-status"';
  if (input.isRead) {
    return '<a href="' + href + '"' + describedBy + ' class="' + className + '">' + content + '</a>';
  }
  return '<a href="' + href + '"' + describedBy + ' onclick="return markNotificationLinkRead(event, ' + input.id + ', this.href)" class="' + className + '">' + content + '</a>';
}

export function renderNotificationActionStatus(input: { id: number; link?: string | null; isRead: boolean }): string {
  const action = notificationDestinationAction(input.link);
  const status = input.link && !input.isRead
    ? action.statusLabel + '; read state syncs in background'
    : action.statusLabel;
  return '<span id="notif-' + escapeNotificationAttr(String(input.id)) + '-action-status" class="text-xs text-zinc-600" aria-live="polite">' +
    escapeNotificationAttr(action.destinationLabel + ' · ' + status) +
  '</span>';
}

export function renderNotifications(items: readonly Notification[], options: { filter?: NotificationFilter } = {}): string {
  if (!items || !items.length) {
    const emptyText = options.filter === 'unread' ? 'No unread notifications' : 'No notifications yet';
    const recoveryAction = options.filter === 'unread'
      ? '<button type="button" onclick="setNotificationFilter(\'all\')" class="btn-ghost mt-4 text-xs"><i class="fas fa-list mr-1" aria-hidden="true"></i>View all notifications</button>'
      : '';
    return '<div class="text-center py-20">' +
      '<i class="fas fa-bell-slash text-4xl text-zinc-700 mb-4" aria-hidden="true"></i>' +
      '<p class="text-zinc-500">' + emptyText + '</p>' +
      recoveryAction +
      '</div>';
  }

  return items.map(function(n) {
    const unreadClass = n.is_read ? '' : 'border-l-2 border-brand-500';
    const bgClass = n.is_read ? 'bg-zinc-900/30' : 'bg-zinc-800/50';
    const iconClass = notificationIcon(n.type);
    const timeStr = timeAgo(n.created_at);

    return '<div class="card p-4 mb-3 ' + bgClass + ' ' + unreadClass + '" id="notif-' + n.id + '">' +
      '<div class="flex items-start gap-3">' +
        '<div class="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center shrink-0">' +
          '<i class="fas ' + iconClass + ' text-sm" aria-hidden="true"></i>' +
        '</div>' +
        '<div class="flex-1 min-w-0">' +
          '<div class="flex items-start justify-between gap-2">' +
            '<div>' +
              '<p class="text-sm font-medium text-zinc-200">' + escapeHtml(n.title) + '</p>' +
              (n.body ? '<p class="text-xs text-zinc-500 mt-1">' + escapeHtml(n.body) + '</p>' : '') +
            '</div>' +
            '<span class="text-xs text-zinc-600 whitespace-nowrap">' + timeStr + '</span>' +
          '</div>' +
          '<div class="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">' +
            renderNotificationViewLink({ id: n.id, link: n.link, isRead: n.is_read }) +
            renderNotificationActionStatus({ id: n.id, link: n.link, isRead: n.is_read }) +
            (!n.is_read ? '<button onclick="markOneRead(' + n.id + ')" class="text-xs text-zinc-600 hover:text-zinc-400">Mark read</button>' : '') +
          '</div>' +
        '</div>' +
      '</div>' +
      '</div>';
  }).join('');
}

async function loadNotifications(filter: NotificationFilter = 'all'): Promise<DataResult<NotificationsViewData>> {
  return readApiResult(
    () => api.users.getNotifications(filter === 'unread' ? { unread_only: true } : {}),
    'Failed to load notifications',
    {
      map: (data) => {
        const apiData = data as NotificationsApiData;
        const items = [...(apiData.items || [])];
        return {
          items,
          total: apiData.total,
          unreadCount: apiData.unread_count ?? items.filter(item => !item.is_read).length,
          filter,
        };
      },
      networkError: 'Failed to load notifications',
    },
  );
}

async function markNotificationRead(notifId: number): Promise<DataResult<NotificationReadResult>> {
  return readApiResult(
    () => api.users.markNotificationRead(notifId),
    'Failed to mark notification as read',
    { networkError: 'Failed to mark notification as read' },
  );
}

function markNotificationReadInBackground(notifId: number): void {
  void api.users.markNotificationRead(notifId, { keepalive: true });
}

async function markAllNotificationsRead(): Promise<DataResult<NotificationsReadAllResult>> {
  return readApiResult(
    () => api.users.markAllNotificationsRead(),
    'Failed to mark all notifications as read',
    { networkError: 'Failed to mark all notifications as read' },
  );
}

if (typeof window !== 'undefined') {
  window.NotificationsPageData = {
    notificationFilterFromSearch,
    buildNotificationFilterSearch,
    issueIdFromNotificationLink,
    loadNotifications,
    prefetchIssuePageDataFromNotificationLink,
    markNotificationRead,
    markNotificationReadInBackground,
    markAllNotificationsRead,
  };

  window.NotificationsPageRenderers = {
    notificationDestinationAction,
    renderNotificationActionStatus,
    renderNotificationSummary,
    renderNotifications,
    renderNotificationViewLink,
  };
}
